// =============================================================================
// UserManagement.js - Gestão de Utilizadores
// =============================================================================
// Smart Home - CRUD de utilizadores (Admin only)
// =============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { 
  ArrowLeft, Users, UserPlus, Trash2, Edit, Save, X, 
  Shield, ShieldCheck, Eye, EyeOff, RefreshCw, Search,
  Check, AlertCircle, Building2, KeyRound
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './UserManagement.css';

const UserManagement = ({ apiUrl, onBack, currentUsername }) => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);  // Lista de todas as instalações
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [editingUser, setEditingUser] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    role: 'user',  // 'super_admin', 'admin', 'user'
    email: '',
    full_name: '',
    tenant_ids: []  // IDs das instalações associadas
  });
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${apiUrl}/users?include_inactive=true`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error(t('utilizadores.acessoNegado'));
        }
        throw new Error(`Erro ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      // A API retorna { users: [...], total: N }
      setUsers(data.users || []);
      setError(null);
    } catch (err) {
      console.error('Erro ao buscar utilizadores:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  // Fetch tenants (instalações)
  const fetchTenants = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${apiUrl}/tenants`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // A API pode retornar um array direto ou { tenants: [...] }
        if (Array.isArray(data)) {
          setTenants(data);
        } else if (data && Array.isArray(data.tenants)) {
          setTenants(data.tenants);
        } else {
          setTenants([]);
        }
      }
    } catch (err) {
      console.error('Erro ao buscar instalações:', err);
      setTenants([]);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchUsers();
    fetchTenants();
  }, [fetchUsers, fetchTenants]);

  // Clear messages after timeout
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!formData.username.trim()) {
      errors.username = t('utilizadores.validacao.usernameObrigatorio');
    } else if (formData.username.length < 3) {
      errors.username = t('utilizadores.validacao.usernameCurto');
    }
    
    if (modalMode === 'create') {
      if (!formData.password) {
        errors.password = t('utilizadores.validacao.passwordObrigatoria');
      } else if (formData.password.length < 4) {
        errors.password = t('utilizadores.validacao.passwordCurta');
      }
      
      if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = t('utilizadores.validacao.passwordsDiferentes');
      }
    } else if (formData.password && formData.password !== formData.confirmPassword) {
      errors.confirmPassword = t('utilizadores.validacao.passwordsDiferentes');
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Open create modal
  const openCreateModal = () => {
    setFormData({
      username: '',
      password: '',
      confirmPassword: '',
      role: 'user',
      email: '',
      full_name: '',
      tenant_ids: []
    });
    setFormErrors({});
    setModalMode('create');
    setEditingUser(null);
    setShowModal(true);
  };

  // Open edit modal
  const openEditModal = (user) => {
    setFormData({
      username: user.username,
      password: '',
      confirmPassword: '',
      role: user.role || 'user',
      email: user.email || '',
      full_name: user.full_name || '',
      tenant_ids: user.tenant_ids || []
    });
    setFormErrors({});
    setModalMode('edit');
    setEditingUser(user);
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormErrors({});
  };

  // Create user
  const createUser = async () => {
    if (!validateForm()) return;
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${apiUrl}/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password,
          role: formData.role,
          email: formData.email || null,
          full_name: formData.full_name || null,
          tenant_ids: formData.tenant_ids
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || t('utilizadores.erroCriar'));
      }

      setSuccess(t('utilizadores.criadoSucesso'));
      closeModal();
      fetchUsers();
    } catch (err) {
      setFormErrors({ submit: err.message });
    }
  };

  // Update user
  const updateUser = async () => {
    if (!validateForm()) return;
    
    try {
      const token = localStorage.getItem('access_token');
      const updateData = {
        role: formData.role,
        email: formData.email || null,
        full_name: formData.full_name || null,
        tenant_ids: formData.tenant_ids
      };

      // Atualizar dados básicos
      const response = await fetch(`${apiUrl}/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || t('utilizadores.erroAtualizar'));
      }

      // Se password foi preenchida, atualizar separadamente
      if (formData.password) {
        const pwdResponse = await fetch(`${apiUrl}/users/${editingUser.id}/password`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ new_password: formData.password })
        });

        if (!pwdResponse.ok) {
          const data = await pwdResponse.json();
          throw new Error(data.detail || t('utilizadores.erroPassword'));
        }
      }

      setSuccess(t('utilizadores.atualizadoSucesso'));
      closeModal();
      fetchUsers();
    } catch (err) {
      setFormErrors({ submit: err.message });
    }
  };

  // Delete user
  const deleteUser = async (user) => {
    if (!window.confirm(t('utilizadores.confirmarEliminar', { nome: user.username }))) {
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${apiUrl}/users/${user.id}?permanent=true`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || t('utilizadores.erroEliminar'));
      }

      setSuccess(t('utilizadores.eliminadoSucesso'));
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  // Repor o MFA de alguém que perdeu o telemóvel e os códigos de recuperação.
  // Volta a configurá-lo no login seguinte. O backend recusa repor o próprio
  // e só deixa um super_admin repor outro super_admin.
  const resetMfa = async (user) => {
    if (!window.confirm(t('utilizadores.confirmarReporMfa', { nome: user.username }))) {
      return;
    }
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${apiUrl}/auth/mfa/reset/${encodeURIComponent(user.username)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || t('utilizadores.erroReporMfa'));
      }
      setSuccess(t('utilizadores.mfaReposto', { nome: user.username }));
    } catch (err) {
      setError(err.message);
    }
  };

  // Filter users
  const filteredUsers = users.filter(user => 
    user.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="user-management-container">
        <div className="user-loading">
          <RefreshCw className="spin" size={48} />
          <p>{t('utilizadores.aCarregar')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management-container">
      {/* Header */}
      <div className="user-header">
        <button className="back-button" onClick={onBack}>
          <ArrowLeft size={24} />
          <span>{t('comum.voltar')}</span>
        </button>
        <h1><Users size={32} /> {t('utilizadores.titulo')}</h1>
        <button className="btn-refresh" onClick={fetchUsers}>
          <RefreshCw size={20} />
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="message error">
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={() => setError(null)}><X size={16} /></button>
        </div>
      )}
      
      {success && (
        <div className="message success">
          <Check size={20} />
          <span>{success}</span>
        </div>
      )}

      {/* Toolbar */}
      <div className="user-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder={t('utilizadores.pesquisar')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="btn-create" onClick={openCreateModal}>
          <UserPlus size={20} />
          <span>{t('utilizadores.novoUtilizador')}</span>
        </button>
      </div>

      {/* Users List */}
      <div className="users-list">
        {filteredUsers.length === 0 ? (
          <div className="no-users">
            <Users size={48} />
            <p>{t('utilizadores.nenhumEncontrado')}</p>
          </div>
        ) : (
          <table className="users-table">
            <thead>
              <tr>
                <th>{t('utilizadores.colUsername')}</th>
                <th>{t('utilizadores.colFuncao')}</th>
                <th>{t('utilizadores.colInstalacoes')}</th>
                <th>{t('utilizadores.colEstado')}</th>
                <th>{t('utilizadores.colAccoes')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user.id}>
                  <td className="user-name">
                    <span className="user-avatar">
                      {user.username.charAt(0).toUpperCase()}
                    </span>
                    <div className="user-info">
                      <span className="username">{user.username}</span>
                      {user.full_name && <span className="fullname">{user.full_name}</span>}
                    </div>
                  </td>
                  <td>
                    {user.role === 'super_admin' ? (
                      <span className="badge super-admin">
                        <ShieldCheck size={14} /> {t('utilizadores.roles.superAdmin')}
                      </span>
                    ) : user.role === 'admin' ? (
                      <span className="badge admin">
                        <ShieldCheck size={14} /> {t('utilizadores.roles.admin')}
                      </span>
                    ) : (
                      <span className="badge user">
                        <Shield size={14} /> {t('utilizadores.roles.user')}
                      </span>
                    )}
                  </td>
                  <td className="tenant-cell">
                    {user.tenant_names && Object.keys(user.tenant_names).length > 0 
                      ? Object.values(user.tenant_names).join(', ')
                      : '-'
                    }
                  </td>
                  <td>
                    <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                      {user.is_active ? t('utilizadores.ativo') : t('utilizadores.inativo')}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <button 
                      className="btn-edit" 
                      onClick={() => openEditModal(user)}
                      title={t('utilizadores.editar')}
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      className="btn-edit"
                      onClick={() => resetMfa(user)}
                      title={t('utilizadores.reporMfa')}
                      disabled={user.username === currentUsername}
                    >
                      <KeyRound size={16} />
                    </button>
                    <button 
                      className="btn-delete" 
                      onClick={() => deleteUser(user)}
                      title={t('utilizadores.eliminar')}
                      disabled={user.username === 'admin'}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {modalMode === 'create' ? (
                  <><UserPlus size={24} /> {t('utilizadores.novoUtilizador')}</>
                ) : (
                  <><Edit size={24} /> {t('utilizadores.editarUtilizador')}</>
                )}
              </h2>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>

            <div className="modal-body">
              {formErrors.submit && (
                <div className="form-error global">
                  <AlertCircle size={16} />
                  {formErrors.submit}
                </div>
              )}

              <div className="form-group">
                <label>{t('utilizadores.labelUsername')}</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  placeholder={t('utilizadores.placeholderUsername')}
                  disabled={modalMode === 'edit'}
                  className={formErrors.username ? 'error' : ''}
                />
                {formErrors.username && (
                  <span className="field-error">{formErrors.username}</span>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('utilizadores.nomeCompleto')}</label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    placeholder={t('utilizadores.placeholderNomeCompleto')}
                  />
                </div>

                <div className="form-group">
                  <label>{t('utilizadores.email')}</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder={t('utilizadores.placeholderEmail')}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>
                  {modalMode === 'create' ? t('utilizadores.password') : t('utilizadores.novaPassword')}
                </label>
                <div className="password-input">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    placeholder={modalMode === 'create' ? t('utilizadores.placeholderPassword') : t('utilizadores.placeholderNovaPassword')}
                    className={formErrors.password ? 'error' : ''}
                  />
                  <button 
                    type="button" 
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {formErrors.password && (
                  <span className="field-error">{formErrors.password}</span>
                )}
              </div>

              <div className="form-group">
                <label>{t('utilizadores.confirmarPassword')}</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  placeholder={t('utilizadores.placeholderConfirmar')}
                  className={formErrors.confirmPassword ? 'error' : ''}
                />
                {formErrors.confirmPassword && (
                  <span className="field-error">{formErrors.confirmPassword}</span>
                )}
              </div>

              <div className="form-group">
                <label>{t('utilizadores.funcaoRole')}</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="role-select"
                >
                  <option value="user">{t('utilizadores.roles.opcaoUser')}</option>
                  <option value="admin">{t('utilizadores.roles.opcaoAdmin')}</option>
                  <option value="super_admin">{t('utilizadores.roles.opcaoSuperAdmin')}</option>
                </select>
                <p className="help-text">
                  {formData.role === 'super_admin' && t('utilizadores.roles.ajudaSuperAdmin')}
                  {formData.role === 'admin' && t('utilizadores.roles.ajudaAdmin')}
                  {formData.role === 'user' && t('utilizadores.roles.ajudaUser')}
                </p>
              </div>

              {/* Instalações Associadas */}
              <div className="form-group tenants-section">
                <label><Building2 size={16} /> {t('utilizadores.instalacoesAssociadas')}</label>
                <div className="tenants-list">
                  {!Array.isArray(tenants) || tenants.length === 0 ? (
                    <p className="no-tenants">{t('utilizadores.nenhumaInstalacao')}</p>
                  ) : (
                    tenants.map(tenant => {
                      const tenantId = tenant._id || tenant.id;
                      const isChecked = Array.isArray(formData.tenant_ids) && formData.tenant_ids.includes(tenantId);
                      return (
                        <label key={tenantId} className="tenant-checkbox">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              const currentIds = Array.isArray(formData.tenant_ids) ? formData.tenant_ids : [];
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  tenant_ids: [...currentIds, tenantId]
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  tenant_ids: currentIds.filter(id => id !== tenantId)
                                });
                              }
                            }}
                          />
                          <span className="tenant-name">{tenant.name}</span>
                        </label>
                      );
                    })
                  )}
                </div>
                <p className="help-text">
                  {t('utilizadores.ajudaInstalacoes')}
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={closeModal}>
                <X size={18} /> {t('comum.cancelar')}
              </button>
              <button 
                className="btn-save" 
                onClick={modalMode === 'create' ? createUser : updateUser}
              >
                <Save size={18} /> {modalMode === 'create' ? t('utilizadores.criar') : t('comum.guardar')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
